import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { SubCategory, GalenicForm } from '../../types/category'
import { CategoriesService } from '../../services/api/categoriesService'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface SubCategoryDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (subCategory: SubCategory) => Promise<void>
  title: string
  categoryName: string
  existingSubCategory?: SubCategory
}

export const SubCategoryDialog: React.FC<SubCategoryDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  categoryName,
  existingSubCategory
}) => {
  const [name, setName] = useState(existingSubCategory?.name || '')
  const [availableGalenicForms, setAvailableGalenicForms] = useState<GalenicForm[]>([])
  const [selectedGalenicForms, setSelectedGalenicForms] = useState<GalenicForm[]>(
    existingSubCategory?.galenicForms || []
  )
  const [originalGalenicForms, setOriginalGalenicForms] = useState<GalenicForm[]>(
    existingSubCategory?.galenicForms || []
  )
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchGalenicForms()
    }
  }, [isOpen])

  useEffect(() => {
    if (existingSubCategory) {
      console.log('Loading existing subcategory:', existingSubCategory)
      setName(existingSubCategory.name)
      setSelectedGalenicForms(existingSubCategory.galenicForms || [])
      setOriginalGalenicForms(existingSubCategory.galenicForms || [])
    } else {
      setName('')
      setSelectedGalenicForms([])
      setOriginalGalenicForms([])
    }
  }, [existingSubCategory])

  const fetchGalenicForms = async () => {
    setLoading(true)
    try {
      const forms = await CategoriesService.getGalenicForms()
      console.log('Galenic forms fetched:', forms)
      setAvailableGalenicForms(forms)
    } catch (error) {
      console.error('Error fetching galenic forms:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleGalenicForm = (form: GalenicForm) => {
    const isSelected = selectedGalenicForms.some(f => f.id === form.id)
    const isOriginal = originalGalenicForms.some(f => f.id === form.id)
    
    if (isSelected) {
      // Can only deselect if it's not an original form
      if (!isOriginal) {
        setSelectedGalenicForms(selectedGalenicForms.filter(f => f.id !== form.id))
      }
    } else {
      setSelectedGalenicForms([...selectedGalenicForms, form])
    }
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      return
    }

    setSaving(true)
    try {
      const subCategory: SubCategory = {
        id: existingSubCategory?.id,
        name: name.trim(),
        galenicForms: selectedGalenicForms
      }
      await onSubmit(subCategory)
      onClose()
    } catch (error) {
      console.error('Error submitting subcategory:', error)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Catégorie: <span className="font-medium">{categoryName}</span>
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="subcategory-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
              Nom de la sous-catégorie <span className="text-red-500">*</span>
            </label>
            <Input
              id="subcategory-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Entrez le nom de la sous-catégorie"
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="galenic-forms" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
              Formes galéniques
            </label>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div id="galenic-forms" className="flex flex-wrap gap-2">
                {availableGalenicForms.map((form) => {
                  const isSelected = selectedGalenicForms.some(f => f.id === form.id)
                  const isOriginal = originalGalenicForms.some(f => f.id === form.id)
                  const isLocked = isSelected && isOriginal
                  
                  return (
                    <button
                      key={form.id}
                      onClick={() => toggleGalenicForm(form)}
                      disabled={isLocked}
                      className={`
                        px-3 py-1.5 rounded-full text-sm transition-colors relative
                        ${isSelected
                          ? isLocked 
                            ? 'bg-sk-gold/70 text-sk-black cursor-not-allowed'
                            : 'bg-sk-gold text-sk-black hover:bg-sk-gold/90'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }
                      `}
                      title={isLocked ? 'Cette forme galénique ne peut pas être retirée' : undefined}
                    >
                      {form.displayName || form.name || `Form ${form.id}`}
                      {isLocked && (
                        <span className="ml-1 text-xs">🔒</span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
            {existingSubCategory && originalGalenicForms.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                Note : Les formes galéniques existantes (🔒) ne peuvent pas être retirées car des produits peuvent y être associés.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
          >
            {saving ? 'Enregistrement...' : existingSubCategory ? 'Modifier' : 'Ajouter'}
          </Button>
        </div>
      </div>
    </div>
  )
}